import { type AanvragenPolissenGetWagenparkcontractafsprakenVoertuigsoortDto as VoertuigsoortDto } from '@services/polis-public/orval-api/model/aanvragenPolissenGetWagenparkcontractafsprakenVoertuigsoortDto';
import { type AanvragenPolissenGetWagenparkcontractafsprakenWagenparkafspraakDto as WagenparkafspraakDto } from '@services/polis-public/orval-api/model/aanvragenPolissenGetWagenparkcontractafsprakenWagenparkafspraakDto';
import { tryFormatDate } from '@tools/dates';
import { currencyFormatter, groupBy } from '@tools/helpers';

type Formaat = 'bedrag' | 'percentage';

export type AfspraakKolom = {
  key: string;
  label: string;
  formaat: Formaat;
  waarden: (afspraak: WagenparkafspraakDto) => Array<number | null | undefined>;
};

export const afspraakKolommen: AfspraakKolom[] = [
  {
    key: 'eigenRisico',
    label: 'Eigen risico',
    formaat: 'bedrag',
    waarden: (afspraak) => [
      afspraak.eigenRisicoWa,
      afspraak.eigenRisicoBeperktCasco,
      afspraak.eigenRisicoCasco,
    ],
  },
  {
    key: 'extraEigenRisicoCasco',
    label: 'Extra eigen risico casco',
    formaat: 'bedrag',
    waarden: (afspraak) => [afspraak.extraEigenRisicoCasco],
  },
  {
    key: 'eigenRisicopercentageCasco',
    label: 'Eigen risico casco %',
    formaat: 'percentage',
    waarden: (afspraak) => [afspraak.eigenRisicopercentageCasco],
  },
  {
    key: 'kortingspercentage',
    label: 'Korting',
    formaat: 'percentage',
    waarden: (afspraak) => [afspraak.kortingspercentage],
  },
  {
    key: 'kortingsbedrag',
    label: 'Kortingsbedrag',
    formaat: 'bedrag',
    waarden: (afspraak) => [afspraak.kortingsbedrag],
  },
  {
    key: 'vastePremie',
    label: 'Vaste premie',
    formaat: 'bedrag',
    waarden: (afspraak) => [afspraak.vastePremie],
  },
  {
    key: 'vastePremieWa',
    label: 'Vaste premie WA',
    formaat: 'bedrag',
    waarden: (afspraak) => [afspraak.vastePremieWa],
  },
  {
    key: 'percentageDagOfCataloguswaarde',
    label: '% dag-/cataloguswaarde',
    formaat: 'percentage',
    waarden: (afspraak) => [afspraak.percentageDagOfCataloguswaarde],
  },
];

export type DekkingRij = {
  key: string;
  label: string;
  product?: string;
  periode?: string;
  waarden: Record<string, number[]>;
};

type Afsprakenrij = {
  code: number;
  dekking: string;
  productIndex: number;
  product: string;
  periode: string;
  toonPeriode: boolean;
  waarden: Record<string, number[]>;
};

export function isLegeWaarde(waarde: number | null | undefined): waarde is 0 | null | undefined {
  return waarde == null || waarde === 0;
}

function waardenVan(afspraak: WagenparkafspraakDto): Record<string, number[]> {
  return Object.fromEntries(
    afspraakKolommen.map((kolom) => [
      kolom.key,
      [
        ...new Set(
          kolom.waarden(afspraak).filter((waarde): waarde is number => !isLegeWaarde(waarde)),
        ),
      ],
    ]),
  );
}

function heeftAfspraken(waarden: Record<string, number[]>): boolean {
  return Object.values(waarden).some((waarde) => waarde.length > 0);
}

function productnaam(product: VoertuigsoortDto['producten'][number]): string {
  const naam = product.omschrijving?.trim() || `${product.soortVerzekering} ${product.productcode}`;

  return naam.replace(/\s*\((?:wagenpark|wp)\)$/i, '');
}

export function maakDekkingRijen(voertuigsoort: VoertuigsoortDto): DekkingRij[] {
  const heeftMeerdereProducten = voertuigsoort.producten.length > 1;

  const afsprakenrijen: Afsprakenrij[] = voertuigsoort.producten
    .flatMap((product, productIndex) =>
      product.dekkingen.flatMap((dekking) =>
        dekking.afspraken.map((afspraak) => ({
          code: dekking.code,
          dekking: dekking.naam?.trim() || `Dekking ${dekking.code}`,
          productIndex,
          product: productnaam(product),
          periode: formatPeriode(afspraak.ingangsdatum, afspraak.einddatum),
          toonPeriode: dekking.afspraken.length > 1,
          waarden: waardenVan(afspraak),
        })),
      ),
    )
    .filter((rij) => heeftAfspraken(rij.waarden));

  const productenPerDekking = new Map(
    groupBy(
      voertuigsoort.producten.flatMap((product, productIndex) =>
        product.dekkingen.map((dekking) => ({ code: dekking.code, productIndex })),
      ),
      (item) => item.code,
    ).map(({ key, items }) => [key, new Set(items.map((item) => item.productIndex)).size]),
  );

  return groupBy(
    afsprakenrijen,
    (rij) => `${rij.code}|${rij.periode}|${JSON.stringify(rij.waarden)}`,
  )
    .map(({ items }) => {
      const [eerste] = items;
      const producten = [...new Set(items.map((rij) => rij.product))];
      const geldtVoorAlleProducten = producten.length === productenPerDekking.get(eerste.code);

      return {
        key: `${eerste.code}-${eerste.periode}-${items.map((rij) => rij.productIndex).join('-')}`,
        label: eerste.dekking,
        product:
          heeftMeerdereProducten && !geldtVoorAlleProducten ? producten.join(' · ') : undefined,
        periode: eerste.toonPeriode ? eerste.periode : undefined,
        waarden: eerste.waarden,
        code: eerste.code,
        volgorde: Math.min(...items.map((rij) => rij.productIndex)),
      };
    })
    .sort((a, b) => a.code - b.code || a.volgorde - b.volgorde)
    .map(({ code, volgorde, ...rij }) => rij);
}

export function zichtbareKolommen(rijen: DekkingRij[]): AfspraakKolom[] {
  return afspraakKolommen.filter((kolom) =>
    rijen.some((rij) => (rij.waarden[kolom.key]?.length ?? 0) > 0),
  );
}

const percentageFormatter = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 });

function formatWaarde(waarde: number, formaat: Formaat): string {
  return formaat === 'bedrag'
    ? currencyFormatter(waarde)
    : `${percentageFormatter.format(waarde)}%`;
}

export function formatCel(rij: DekkingRij, kolom: AfspraakKolom): string {
  const waarden = rij.waarden[kolom.key] ?? [];

  return waarden.map((waarde) => formatWaarde(waarde, kolom.formaat)).join(' / ');
}

export function formatPeriode(ingangsdatum: string, einddatum: string): string {
  return `${tryFormatDate(ingangsdatum)} t/m ${tryFormatDate(einddatum)}`;
}

export function parseContractnummer(
  internPolisnummer: string | null | undefined,
): number | undefined {
  const waarde = internPolisnummer?.trim() ?? '';

  return /^\d+$/.test(waarde) ? Number(waarde) : undefined;
}
